/**
 * `WebRTCManager` —— 直连通道（迁移自 `prototype/web/core/webrtc.js`，逻辑不动，TS 化 + 状态回调）。
 *
 * 定位：三级回退里的第 ① 级。失败不抛错，由 `HybridTransport` 回落到中继。
 * 信令通道复用聊天信封 `kind:'signal'`（经中继转发，见 `HybridTransport`）。
 */

import { createLogger } from '@/lib/logger';

const logger = createLogger('net:webrtc');

/** 公共 STUN（仅打洞，不转发任何业务数据）。 */
export const STUN_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

/** DataChannel 标签（两端必须一致）。 */
const DATA_CHANNEL_LABEL = 'meshref-dc';

/** 15 秒连不上就判定失败，回落中继。 */
const CONNECT_TIMEOUT_MS = 15_000;
/** ICE 收集最多等 2 秒（等太久会拖慢首连）。 */
const ICE_GATHER_TIMEOUT_MS = 2_000;

/** WebRTC 连接状态（与 `ConnStatus` 的 direct 一档对应）。 */
export type RtcStatus = 'idle' | 'connecting' | 'connected' | 'failed';

export interface WebRTCSignal {
  type: 'webrtc-offer' | 'webrtc-answer' | 'webrtc-ice';
  sdp?: string;
  candidate?: string;
  sdpMid?: string;
  sdpMLineIndex?: number;
}

export interface WebRTCManagerOptions {
  /** 发送信令给对端（经中继 `kind:'signal'`）。 */
  sendSignal(toPeerId: string, signal: WebRTCSignal): void;
  /** 收到对端直连消息（已是完整信封对象）。 */
  onMessage(peerId: string, data: unknown): void;
  onStatusChange(peerId: string, status: RtcStatus): void;
}

interface RtcEntry {
  pc: RTCPeerConnection;
  dc: RTCDataChannel | null;
  status: RtcStatus;
  role: 'offerer' | 'answerer';
}

/** 每个好友一个 `RTCPeerConnection`，支持直连发送与 offer/answer/ICE 交换。 */
export class WebRTCManager {
  private readonly connections = new Map<string, RtcEntry>();

  constructor(private readonly opts: WebRTCManagerOptions) {}

  /** 当前状态（无记录返回 idle）。 */
  getStatus(peerId: string): RtcStatus {
    return this.connections.get(peerId)?.status ?? 'idle';
  }

  /** 直连是否真正可用（DataChannel 已 open）。 */
  isDirect(peerId: string): boolean {
    const conn = this.connections.get(peerId);
    return !!conn && conn.status === 'connected' && !!conn.dc && conn.dc.readyState === 'open';
  }

  /** 所有处于直连的对端。 */
  directPeers(): string[] {
    return [...this.connections.keys()].filter((p) => this.isDirect(p));
  }

  /** 主动发起直连（offerer）。 */
  async connect(peerId: string): Promise<boolean> {
    const existing = this.connections.get(peerId);
    if (existing) {
      if (existing.status === 'connected' && existing.dc?.readyState === 'open') return true;
      if (existing.status === 'connecting') return false;
      this.close(peerId);
    }

    const pc = this.createPeerConnection(peerId);
    const dc = pc.createDataChannel(DATA_CHANNEL_LABEL, { ordered: true });
    this.setupDataChannel(dc, peerId);

    this.connections.set(peerId, { pc, dc, status: 'connecting', role: 'offerer' });
    this.setStatus(peerId, 'connecting');

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await this.waitForIce(pc);
      this.opts.sendSignal(peerId, { type: 'webrtc-offer', sdp: pc.localDescription?.sdp ?? '' });
      logger.debug('已发送 offer', peerId.slice(0, 12));

      const ok = await this.waitForConnection(peerId);
      this.setStatus(peerId, ok ? 'connected' : 'failed');
      logger.info(ok ? '直连建立成功' : '直连失败，回落中继', peerId.slice(0, 12));
      return ok;
    } catch (err) {
      logger.warn('直连异常', peerId.slice(0, 12), err instanceof Error ? err.message : err);
      this.setStatus(peerId, 'failed');
      return false;
    }
  }

  /** 处理对端信令（被动方建连 / answer / ICE）。 */
  async handleSignal(fromPeerId: string, signal: WebRTCSignal): Promise<void> {
    if (!signal?.type) return;
    logger.debug('收到信令', signal.type, fromPeerId.slice(0, 12));

    if (signal.type === 'webrtc-offer') {
      const answer = await this.createAnswerer(fromPeerId, signal.sdp ?? '');
      if (answer === null) return;
      this.opts.sendSignal(fromPeerId, { type: 'webrtc-answer', sdp: answer });
      const ok = await this.waitForConnection(fromPeerId);
      this.setStatus(fromPeerId, ok ? 'connected' : 'failed');
      logger.info(ok ? '直连建立成功（被动方）' : '直连失败（被动方）', fromPeerId.slice(0, 12));
      return;
    }

    const conn = this.connections.get(fromPeerId);
    if (!conn?.pc) return;

    if (signal.type === 'webrtc-answer') {
      try {
        await conn.pc.setRemoteDescription({ type: 'answer', sdp: signal.sdp ?? '' });
      } catch (err) {
        logger.debug('setRemoteDescription(answer) 失败', err instanceof Error ? err.message : err);
      }
      return;
    }

    if (signal.type === 'webrtc-ice' && conn.pc.remoteDescription) {
      try {
        await conn.pc.addIceCandidate({
          candidate: signal.candidate ?? '',
          sdpMid: signal.sdpMid ?? undefined,
          sdpMLineIndex: signal.sdpMLineIndex ?? undefined,
        });
      } catch {
        // ICE candidate 过期属常态，忽略
      }
    }
  }

  /** 通过直连发送（失败返回 false，由上层回落中继）。 */
  send(peerId: string, data: unknown): boolean {
    const conn = this.connections.get(peerId);
    if (!conn?.dc || conn.dc.readyState !== 'open') return false;
    try {
      conn.dc.send(JSON.stringify(data));
      return true;
    } catch (err) {
      logger.warn('直连发送失败', err instanceof Error ? err.message : err);
      return false;
    }
  }

  /** 关闭单个对端连接。 */
  close(peerId: string): void {
    const conn = this.connections.get(peerId);
    if (!conn) return;
    try {
      conn.dc?.close();
    } catch {
      /* noop */
    }
    try {
      conn.pc.close();
    } catch {
      /* noop */
    }
    this.connections.delete(peerId);
    this.setStatus(peerId, 'idle');
  }

  /** 关闭全部连接（锁定 / 注销时调用）。 */
  closeAll(): void {
    for (const peerId of [...this.connections.keys()]) this.close(peerId);
  }

  // ─────────────── 内部 ───────────────

  /**
   * 被动建连核心：`handleSignal` 的 offer 分支使用。
   *
   * **纯建连**：只产出 answer SDP，**不**负责发送与等待 —— 信令流的发送/等待由
   * `handleSignal` 统一执行（避免 answer 被重复外发），状态交给 DataChannel
   * open/failed 事件推进（`setupDataChannel` / `setStatus`）。
   *
   * @param peerId 对端 PeerID（连接键）
   * @param offerSdp 对端 offer SDP
   * @returns answer SDP；失败 `null`
   */
  private async createAnswerer(peerId: string, offerSdp: string): Promise<string | null> {
    if (this.connections.has(peerId)) this.close(peerId);

    const pc = this.createPeerConnection(peerId);
    pc.ondatachannel = (e) => {
      this.setupDataChannel(e.channel, peerId);
      const conn = this.connections.get(peerId);
      if (conn) conn.dc = e.channel;
    };
    this.connections.set(peerId, { pc, dc: null, status: 'connecting', role: 'answerer' });
    this.setStatus(peerId, 'connecting');

    try {
      await pc.setRemoteDescription({ type: 'offer', sdp: offerSdp });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await this.waitForIce(pc);
      const sdp = pc.localDescription?.sdp ?? '';
      if (!sdp) throw new Error('createAnswer 产出空 SDP');
      return sdp;
    } catch (err) {
      logger.warn('处理 offer 失败', err instanceof Error ? err.message : err);
      this.setStatus(peerId, 'failed');
      return null;
    }
  }

  /** 建 PC 并装配 ICE candidate 增量外发与连接状态监听。 */
  private createPeerConnection(peerId: string): RTCPeerConnection {
    const pc = new RTCPeerConnection({ iceServers: STUN_SERVERS });

    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      this.opts.sendSignal(peerId, {
        type: 'webrtc-ice',
        candidate: e.candidate.candidate,
        sdpMid: e.candidate.sdpMid ?? undefined,
        sdpMLineIndex: e.candidate.sdpMLineIndex ?? undefined,
      });
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        this.setStatus(peerId, 'failed');
      }
    };

    return pc;
  }

  /** 装配 DataChannel 回调。 */
  private setupDataChannel(dc: RTCDataChannel, peerId: string): void {
    dc.onopen = () => {
      logger.debug('DataChannel 已打开', peerId.slice(0, 12));
      this.setStatus(peerId, 'connected');
    };
    dc.onmessage = (e) => {
      try {
        this.opts.onMessage(peerId, JSON.parse(String(e.data)));
      } catch (err) {
        logger.warn('直连消息解析失败', err instanceof Error ? err.message : err);
      }
    };
    dc.onclose = () => {
      this.setStatus(peerId, 'idle');
    };
    dc.onerror = (e) => {
      logger.warn('DataChannel 错误', peerId.slice(0, 12), e);
    };
  }

  private setStatus(peerId: string, status: RtcStatus): void {
    const conn = this.connections.get(peerId);
    if (conn) conn.status = status;
    this.opts.onStatusChange(peerId, status);
  }

  private waitForIce(pc: RTCPeerConnection): Promise<void> {
    if (pc.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, ICE_GATHER_TIMEOUT_MS);
      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === 'complete') {
          clearTimeout(timer);
          resolve();
        }
      };
    });
  }

  private waitForConnection(peerId: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const conn = this.connections.get(peerId);
      if (!conn) {
        resolve(false);
        return;
      }
      let settled = false;
      const finish = (ok: boolean): void => {
        if (settled) return;
        settled = true;
        clearInterval(interval);
        clearTimeout(timer);
        resolve(ok);
      };

      const check = (): void => {
        if (conn.dc && conn.dc.readyState === 'open') finish(true);
        else if (conn.pc.connectionState === 'failed') finish(false);
      };

      const interval = setInterval(check, 500);
      const timer = setTimeout(() => finish(!!conn.dc && conn.dc.readyState === 'open'), CONNECT_TIMEOUT_MS);

      const prevOnOpen = conn.dc?.onopen ?? null;
      if (conn.dc) {
        conn.dc.onopen = (e) => {
          if (prevOnOpen && conn.dc) prevOnOpen.call(conn.dc, e);
          finish(true);
        };
      }
      check();
    });
  }
}

export default WebRTCManager;
